import { HubConnectionBuilder, IRetryPolicy } from "@microsoft/signalr"
import { queryClient } from "../../../queryClient"
import { debugLog } from "@/utils/debugLog"
import {
  ChatDto,
  ChatPostDto,
  ChatHubEventContract as ChatHubEventContract,
} from "../../../orval/gen/model"
import { registerHubHandlers } from "../../../utils/registerHubHandler"
import { InfiniteData } from "@tanstack/react-query"
import { notifications } from "@mantine/notifications"
import { router } from "../../../router"
import { UserDto } from "../../../@client/Theasoft_ApiServer_Users"
import {
  getGetChatByIdQueryKey,
  getGetChatPostsQueryKey,
  getGetChatsQueryKey,
  getGetFeedPostQueryKey,
  getGetFeedPostsForUserQueryKey,
  getGetFeedPostsQueryKey,
  getGetUnreadCountQueryKey,
  getUserHasArchivedChatQueryKey,
} from "@/orval/gen/api/chat/chat"
import { userNotificationsEnabeledKey } from "@/utils/local-storage-keys"
import { Pagination } from "@/@client/Theasoft_Contracts_Requests"

const reconnectPolicy: IRetryPolicy = {
  nextRetryDelayInMilliseconds: (ctx) => {
    const retrySeconds = [0, 2, 10, 30]
    const count = ctx.previousRetryCount
    const retryArrLength = retrySeconds.length
    const index = count < retryArrLength - 1 ? count : retryArrLength - 1

    return retrySeconds.at(index) ?? null
  },
}

export const chatHubConnection = new HubConnectionBuilder()
  .withUrl(window.location.origin + "/chatHub")
  .withAutomaticReconnect(reconnectPolicy)
  .build()

export const initializeSignalR = async (): Promise<void> => {
  try {
    await chatHubConnection.start()
    // DebugLog("✅ SignalR connection established.")

    registerHubHandlers<ChatHubEventContract>(chatHubConnection, {
      chatUpdated(updatedChat) {
        queryClient.invalidateQueries({
          queryKey: getGetUnreadCountQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getGetChatsQueryKey(),
        })
        queryClient
          .getQueryCache()
          .findAll({
            queryKey: getGetChatsQueryKey(),
          })
          .forEach((query) => {
            queryClient.setQueryData<InfiniteData<Pagination<ChatDto>>>(
              query.queryKey,
              (oldData) => {
                if (!oldData) return oldData

                const updatedPages = oldData.pages.map((page) => {
                  const foundIndex = page.data.findIndex(
                    (chat) => chat.chatId === updatedChat.chatId
                  )
                  if (foundIndex === -1) return page

                  const newData = [...page.data]
                  newData[foundIndex] = updatedChat

                  return {
                    ...page,
                    data: newData,
                  }
                })

                return {
                  ...oldData,
                  pages: updatedPages,
                }
              }
            )
          })
      },
      newChat() {
        queryClient.invalidateQueries({
          queryKey: getGetChatsQueryKey(),
        })
      },

      newMessage(newMessage) {
        debugLog('received "newMessage"', newMessage)
        if (Notification.permission === "denied") {
          const user = queryClient.getQueryData<UserDto | null>([
            "/api/User/GetAuthenticatedUser",
            null,
          ])
          if (
            user &&
            user.userId !== newMessage.userId &&
            newMessage.showNotification
          ) {
            const userNotificationsEnabeled =
              localStorage.getItem(
                `${user.userId}_${userNotificationsEnabeledKey}`
              ) === "true"
            if (userNotificationsEnabeled) {
              notifications.show({
                id: "new-message-" + newMessage.chatId,
                title: newMessage.displayName,
                message: newMessage.postText,
                autoClose: false,
                onClick: () => {
                  router.navigate({
                    to: "/app/com/chat/$chatId",
                    params: { chatId: newMessage.chatId.toString() },
                  })
                },
                style: { cursor: "pointer" },
              })
            }
          }
        }
        queryClient.invalidateQueries({
          queryKey: getGetChatsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getGetUnreadCountQueryKey(),
        })
        const getChatPostsQueryKey = getGetChatPostsQueryKey({
          chatId: newMessage.chatId,
        })
        queryClient
          .getQueryCache()
          .findAll({
            queryKey: getChatPostsQueryKey,
          })
          .forEach((query) => {
            queryClient.setQueryData<InfiniteData<Pagination<ChatPostDto>>>(
              query.queryKey,
              (oldData) => {
                if (!oldData) return oldData

                const firstPage = oldData.pages[0]

                // avoid duplicates
                const alreadyExists = firstPage.data.some(
                  (msg) => msg.chatPostId === newMessage.chatPostId
                )
                if (alreadyExists) return oldData

                const updatedFirstPage = {
                  ...firstPage,
                  data: [newMessage, ...firstPage.data],
                }

                return {
                  ...oldData,
                  pages: [updatedFirstPage, ...oldData.pages.slice(1)],
                }
              }
            )
          })
      },
      messageUpdated(updatedMessage) {
        debugLog('received "messageUpdated"', updatedMessage)
        queryClient.invalidateQueries({
          queryKey: getGetUnreadCountQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getGetChatsQueryKey(),
        })

        queryClient
          .getQueryCache()
          .findAll({
            queryKey: getGetChatPostsQueryKey({
              chatId: updatedMessage.chatId,
            }),
          })
          .forEach((query) => {
            queryClient.setQueryData<InfiniteData<Pagination<ChatPostDto>>>(
              query.queryKey,
              (oldData) => {
                if (!oldData) return oldData

                const updatedPages = oldData.pages.map((page) => {
                  const index = page.data.findIndex(
                    (msg) => msg.chatPostId === updatedMessage.chatPostId
                  )
                  if (index === -1) return page

                  const newData = [...page.data]
                  newData[index] = updatedMessage

                  return {
                    ...page,
                    data: newData,
                  }
                })

                return {
                  ...oldData,
                  pages: updatedPages,
                }
              }
            )
          })
      },

      newPost(newPost: ChatPostDto) {
        debugLog('received "newPost"', newPost)

        const queryCache = queryClient.getQueryCache()
        queryClient.invalidateQueries({
          queryKey: getGetFeedPostsForUserQueryKey(),
        })

        queryClient.invalidateQueries({
          queryKey: getGetChatsQueryKey(),
        })

        queryCache
          .findAll({
            queryKey: getGetFeedPostsQueryKey({ chatId: newPost.chatId }),
          })
          .forEach((query) => {
            queryClient.setQueryData<InfiniteData<Pagination<ChatPostDto>>>(
              query.queryKey,
              (oldData) => {
                if (!oldData) return oldData

                const firstPage = oldData.pages[0]

                const alreadyExists = firstPage.data.some(
                  (post) => post.chatPostId === newPost.chatPostId
                )
                if (alreadyExists) return oldData

                const updatedFirstPage = {
                  ...firstPage,
                  data: [newPost, ...firstPage.data],
                }

                return {
                  ...oldData,
                  pages: [updatedFirstPage, ...oldData.pages.slice(1)],
                }
              }
            )
          })

        queryCache
          .findAll({
            queryKey: getGetFeedPostQueryKey({
              postId: newPost.chatPostId,
            }),
          })
          .forEach((query) => {
            queryClient.setQueryData<ChatPostDto>(query.queryKey, newPost)
          })
      },
      postUpdated(updatedPost: ChatPostDto) {
        debugLog('received "postUpdated"', updatedPost)

        const queryCache = queryClient.getQueryCache()

        queryClient.invalidateQueries({
          queryKey: getGetFeedPostsForUserQueryKey(),
        })

        queryCache
          .findAll({
            queryKey: getGetFeedPostsQueryKey({ chatId: updatedPost.chatId }),
          })
          .forEach((query) => {
            queryClient.setQueryData<InfiniteData<Pagination<ChatPostDto>>>(
              query.queryKey,
              (oldData) => {
                if (!oldData) return oldData

                const updatedPages = oldData.pages.map((page) => {
                  const index = page.data.findIndex(
                    (post) => post.chatPostId === updatedPost.chatPostId
                  )
                  if (index === -1) return page

                  const newData = [...page.data]
                  newData[index] = updatedPost

                  return { ...page, data: newData }
                })

                return { ...oldData, pages: updatedPages }
              }
            )
          })

        queryCache
          .findAll({
            queryKey: getGetFeedPostsForUserQueryKey(),
          })
          .forEach((query) => {
            queryClient.setQueryData<InfiniteData<Pagination<ChatPostDto>>>(
              query.queryKey,
              (oldData) => {
                if (!oldData) return oldData

                const updatedPages = oldData.pages.map((page) => {
                  const index = page.data.findIndex(
                    (post) => post.chatPostId === updatedPost.chatPostId
                  )
                  if (index === -1) return page

                  const newData = [...page.data]
                  newData[index] = updatedPost

                  return { ...page, data: newData }
                })

                return { ...oldData, pages: updatedPages }
              }
            )
          })

        queryCache
          .findAll({
            queryKey: getGetFeedPostQueryKey({
              postId: updatedPost.chatPostId,
            }),
          })
          .forEach((query) => {
            queryClient.setQueryData<ChatPostDto>(query.queryKey, updatedPost)
          })
      },

      chatDeleted(chatId: number) {
        queryClient.invalidateQueries({
          queryKey: getGetChatsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getGetChatByIdQueryKey({ chatId }),
        })
        queryClient.invalidateQueries({
          queryKey: getUserHasArchivedChatQueryKey(),
        })
      },
    })
  } catch (error) {
    debugLog("❌ Error starting SignalR connection:", error)
  }
}
